import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "original",
        "variant",
        "tabButton",
        "tabContent",
        "searchButton",
        "aiButton",
        "searchContent",
        "aiContent",
        "minimumCharacterError",
        "notEnoughDetailsError",
        "submitButton",
        "input",
        "characterCounter",
        "loadingModal",
        "loadingSuccess",
        "loadingWaitingStar",
    ]

    static values = {
        lawyersPageUrl: String,
        country: String
    }

    connect() {
        // DDT-3726: defense-in-depth — bail if this controller ever attaches
        // outside the homepage. The mount lives in [sm_searchhome_form] which
        // is only rendered in index.php, but a stray render (widget/popup/
        // dynamically-injected markup) would otherwise trigger the ddt-3223
        // sidebar variant swap on blog articles and cause ~400px CLS.
        if (!this.element.closest('main.homepage')) {
            return;
        }

        if( "gb" === this.countryValue ){
            this.variantTarget.remove()
            return;
        }

        const homepageFiltersAbTest = {
            id: 'ddt-3223',
            action: 'ddt-3223',
            sample: 100,
            variants: [
                {
                    id: 'original',
                    weight: 50,
                    activate: () => {
                        if (this.hasVariantTarget) {
                            this.variantTarget.remove();
                        }
                    },
                },
                {
                    id: 'variant',
                    weight: 50,
                    activate: () => {
                        if (this.hasOriginalTarget) {
                            this.originalTarget.remove();
                        }

                        // Hide original mobile elements that live outside the
                        // "original" target (legalaid-mobile-new, mobsub,
                        // filters-dropdown) so they don't show alongside the variant.
                        const container = this.element.querySelector('.filtersSearchContainer');
                        if (container) {
                            const mobileEls = container.querySelectorAll('.legalaid-mobile-new, #mobsub, .filters-dropdown');
                            mobileEls.forEach(el => el.style.setProperty('display', 'none', 'important'));
                        }

                        if (this.hasVariantTarget) {
                            this.variantTarget.classList.remove('d-none');

                            this.initializeVariantListeners();
                        }
                    },
                },
            ],
        };

        window.ddAbTest.enableExperiment(homepageFiltersAbTest);
    }

    slugify(text) {
        return text
            .toString()
            .toLowerCase()
            .normalize('NFD')                   // Normalize to decomposed form for accents
            .replace(/[\u0300-\u036f]/g, '')    // Remove accents
            .replace(/[^\w\s-]/g, '')           // Remove special characters
            .replace(/\s+/g, '-')               // Replace spaces with hyphens
            .replace(/--+/g, '-')               // Replace multiple hyphens with single hyphen
            .trim()                             // Trim whitespace
            .replace(/^-+|-+$/g, '');           // Remove leading/trailing hyphens
    }

    initializeVariantListeners() {
        this.searchButtonTarget.addEventListener('click', (e) => {
            e.preventDefault();
            this.searchContentTarget.classList.remove('d-none');
            this.aiContentTarget.classList.add('d-none');
            this.searchButtonTarget.closest('.border').classList.add('active-tab');
            this.aiButtonTarget.closest('.border').classList.remove('active-tab');
        });

        this.aiButtonTarget.addEventListener('click', (e) => {
            e.preventDefault();

            this.searchContentTarget.classList.add('d-none');
            this.aiContentTarget.classList.remove('d-none');
            this.aiButtonTarget.closest('.border').classList.add('active-tab');
            this.searchButtonTarget.closest('.border').classList.remove('active-tab');

            dataLayer.push({
                event: "dataLayer.gaEvent",
                gaEvent: {
                    category: "Search AI",
                    action: "hp_searchAI_tab_click",
                    label: "",
                },
            });
        });

        this.inputTarget.addEventListener('input', () => {
            this.characterCounterTarget.textContent = `${this.inputTarget.value.length}`;
        });

        this.submitButtonTargets.forEach(btn => {
            btn.addEventListener('click', async () => {
                if (window.dataLayer) {
                    window.dataLayer.push({
                        event: "dataLayer.gaEvent",
                        gaEvent: {
                            category: "Search AI",
                            action: "hp_searchAI_submit",
                            label: "",
                        },
                    });

                    if (this.inputTarget.value.length < 10) {
                        window.dataLayer.push({
                            event: "dataLayer.gaEvent",
                            gaEvent: {
                                category: "Search AI",
                                action: "searchAI_error_displayed",
                                label: "",
                            },
                        });

                        this.minimumCharacterErrorTarget.classList.remove('d-none');

                        return;
                    }
                }

                this.minimumCharacterErrorTarget.classList.add('d-none');

                this.submitButtonTargets.forEach(b => {
                    b.classList.add('btn-secondary', 'ai-stars-loading');
                    b.classList.remove('btn-primary');
                });

                this.resetLoadingModal();
                this.showLoadingModal();

                this.runLoadingSteps();

                try {
                    // Abort the request if the network never responds (e.g. ERR_CONNECTION_CLOSED)
                    // so the loading modal does not stay visible indefinitely.
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 25000);

                    let response;
                    try {
                        response = await fetch(window.jfRestUrl( 'justifit/v1/homepage_search_evaluation_is_legal_need_required_with_open_ai' ), {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                description: this.inputTarget.value.trim(),
                            }),
                            signal: controller.signal,
                        });
                    } finally {
                        clearTimeout(timeoutId);
                    }

                    if (!response.ok) {
                        throw new Error('Network response was not ok');
                    }

                    const responseJson = await response.json();

                    if (responseJson.scenario !== 1) {
                        window.dataLayer.push({
                            event: "dataLayer.gaEvent",
                            gaEvent: {
                                category: "Search AI",
                                action: "searchAI_error_displayed",
                                label: "",
                            },
                        });

                        this.closeLoadingModal();
                        this.notEnoughDetailsErrorTarget.classList.remove('d-none');
                    } else {
                        this.showLoadingSuccess();

                        const result = responseJson.result || [];

                        window.dataLayer.push({
                            event: "dataLayer.gaEvent",
                            gaEvent: {
                                category: "Search AI",
                                action: "searchAI_case_validated",
                                label: "",
                            },
                        });

                        const searchParams = new URLSearchParams();

                        if (result.subcategory) {
                            searchParams.append('keywords', result.subcategory);
                        } else if (result.category) {
                            searchParams.append('specialism', result.category);
                        }

                        if (result.city) {
                            searchParams.append('cities', result.city);
                        }

                        if (result.legal_aid) {
                            searchParams.append('aid', 1);
                        }

                        if (result.language) {
                            searchParams.append('language', result.language);
                        }

                        if (result.man) {
                            searchParams.append('masculin', 1);
                        }

                        if (result.woman) {
                            searchParams.append('feminin', 1);
                        }

                        if (result.reply_fast) {
                            searchParams.append('replies_fast', 1);
                        }

                        if (result.tarifs) {
                            searchParams.append('tarifs', 1);
                        }

                        const redirectUrl = new URL(this.lawyersPageUrlValue, window.location.origin);
                        redirectUrl.search = searchParams.toString();

                        setTimeout(() => {
                            window.location.href = redirectUrl.toString();
                        }, 3000);
                    }
                } catch (error) {
                    console.error('Error:', error);
                    this.closeLoadingModal();
                } finally {
                    this.submitButtonTargets.forEach(b => {
                        b.disabled = false;
                        b.classList.add('btn-primary');
                        b.classList.remove('btn-secondary', 'ai-stars-loading');
                    });
                }
            });
        });

        // Detach legal-aid-modal Stimulus controller from variant popup:
        // on listing pages it redirects via URL params, but here the form submit handles navigation.
        const jurisdictionPopup = this.variantTarget.querySelector('[data-controller="legal-aid-modal"]');
        if (jurisdictionPopup) {
            jurisdictionPopup.removeAttribute('data-controller');
            jurisdictionPopup.querySelectorAll('[data-action]').forEach(el => {
                el.removeAttribute('data-action');
            });
            jurisdictionPopup.querySelectorAll('[data-legal-aid-modal-target]').forEach(el => {
                el.removeAttribute('data-legal-aid-modal-target');
            });
        }

        const jurisdictionField = this.variantTarget.querySelector('.filters-jurisdiction-field');
        const jurisdictionCheckbox = this.variantTarget.querySelector('.filters-jurisdiction-checkbox');

        if (jurisdictionField && jurisdictionCheckbox) {
            jurisdictionField.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                // Toggle checkbox visibility
                if (jurisdictionCheckbox.style.display === 'none' || jurisdictionCheckbox.style.display === '') {
                    jurisdictionCheckbox.style.display = 'block';
                } else {
                    jurisdictionCheckbox.style.display = 'none';
                }

                // Track in Google Analytics
                if (window.dataLayer) {
                    window.dataLayer.push({
                        event: "dataLayer.gaEvent",
                        gaEvent: {
                            category: "Listing Search filters",
                            action: "search_legal_aid_filter_click",
                            label: "variant"
                        }
                    });
                }
            });

            // Make checkboxes behave as radio buttons (only one selected at a time)
            const aidCheckboxes = jurisdictionCheckbox.querySelectorAll('input[name="aid"]');
            aidCheckboxes.forEach(checkbox => {
                checkbox.addEventListener('change', (e) => {
                    aidCheckboxes.forEach(cb => {
                        if (cb !== e.target) {
                            cb.checked = false;
                        }
                    });

                    const anyChecked = Array.from(aidCheckboxes).some(cb => cb.checked);
                    if (anyChecked) {
                        jurisdictionField.classList.add('jurisdictionOn');
                    } else {
                        jurisdictionField.classList.remove('jurisdictionOn');
                    }
                });
            });

            // Close popup when clicking outside (matching archive behavior from main.js)
            jurisdictionCheckbox.addEventListener('click', (e) => {
                e.stopPropagation();
            });
            document.body.addEventListener('click', () => {
                jurisdictionCheckbox.style.display = 'none';
            });
        }
    }

    showLoadingModal() {
        if (this.hasLoadingModalTarget) {
            $(this.loadingModalTarget).modal('show');
        }
    }

    closeLoadingModal() {
        if (this.hasLoadingModalTarget) {
            $(this.loadingModalTarget).modal('hide');
        }
    }

    resetLoadingModal() {
        this.loadingSuccessShown = false;

        const steps = this.loadingModalTarget.querySelectorAll('.ai-loading-step');
        steps.forEach(step => {
            step.classList.remove('ai-step-visible');

            const icon = step.querySelector('.ai-step-icon');
            const spinner = step.querySelector('.ai-step-spinner');
            const checkmark = step.querySelector('.ai-step-check');
            const text = step.querySelector('.ai-step-text');

            spinner.classList.add('d-none');
            checkmark.classList.add('d-none');
            icon.classList.remove('d-none');

            text.classList.remove('ai-step-blinking');
            text.classList.remove('ai-step-completed');
            text.classList.remove('text-primary');
            text.classList.remove('text-secondary');

            const originalText = text.dataset.originalText || text.textContent;
            if (!text.dataset.originalText) {
                text.dataset.originalText = originalText;
            }
            if (!originalText.endsWith('…')) {
                text.textContent = originalText + '…';
            }
        });

        if (this.hasLoadingSuccessTarget && !this.loadingSuccessTarget.classList.contains('d-none')) {
            this.loadingSuccessTarget.classList.add('d-none');
        }

        if (this.hasLoadingWaitingStarTarget && !this.loadingWaitingStarTarget.classList.contains('d-none')) {
            this.loadingWaitingStarTarget.classList.add('d-none');
        }
    }

    showLoadingSuccess() {
        this.loadingSuccessShown = true;

        if (this.hasLoadingWaitingStarTarget) {
            this.loadingWaitingStarTarget.classList.add('d-none');
        }

        if (this.hasLoadingSuccessTarget) {
            this.loadingSuccessTarget.classList.remove('d-none');
        }
    }

    async runLoadingSteps() {
        const steps = this.loadingModalTarget.querySelectorAll('.ai-loading-step');
        const stepDuration = 1500;

        for (let i = 0; i < steps.length; i++) {
            const step = steps[i];
            const text = step.querySelector('.ai-step-text');
            const spinner = step.querySelector('.ai-step-spinner');
            const checkmark = step.querySelector('.ai-step-check');

            step.classList.add('ai-step-visible');
            spinner.classList.remove('d-none');
            text.classList.add('ai-step-blinking');
            text.classList.add('text-primary');

            await new Promise(resolve => setTimeout(resolve, stepDuration));

            spinner.classList.add('d-none');
            checkmark.classList.remove('d-none');

            text.classList.remove('ai-step-blinking');
            text.classList.remove('text-primary');

            text.classList.add('ai-step-completed');
            text.classList.add('text-secondary');

            let originalText = text.dataset.originalText || text.textContent;
            originalText = originalText.replace(/…$/, '');
            text.textContent = originalText;
        }

        if (!this.loadingSuccessShown && this.hasLoadingWaitingStarTarget) {
            this.loadingWaitingStarTarget.classList.remove('d-none');
        }
    }
}
